import { STATUS_FILL } from "./theme";

// Everything the UI needs to say about a cohort, derived from the fields an
// admin actually types in. Nothing here is per-cohort content: add a cohort in
// the database and the page describes it correctly without a code change.
// Two states, and only two: a cohort is either taking registrations, or it is
// running and closed — in which case the only thing on offer is the waitlist
// for the next batch.
export const STATUS = {
  open: { label: "Registrations open", action: "enroll", actionLabel: "Join this cohort" },
  in_progress: { label: "Running · registrations closed", action: "waitlist", actionLabel: "Waitlist the next batch" },
};

export const statusOf = (cohort) => STATUS[cohort?.status] || STATUS.in_progress;
export const fillOf = (cohort) => STATUS_FILL[cohort?.status] || STATUS_FILL.in_progress;
export const isEnrollable = (cohort) => statusOf(cohort).action === "enroll";

// Whatever you can still join comes first.
const RANK = { open: 0, in_progress: 1 };
export const byRelevance = (a, b) =>
  (RANK[a.status] ?? 9) - (RANK[b.status] ?? 9) || new Date(a.start_date) - new Date(b.start_date);

export const formatDate = (iso, opts = { day: "numeric", month: "short", year: "numeric" }) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", opts) : "";

export const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
  return Math.round(diff / 86400000);
};

/** "Sat & Sun, 10:00–13:00 IST · one weekday doubts hour" — built, not typed. */
export const formatSchedule = (cohort) => {
  if (!cohort) return "";
  const days = (cohort.session_days || []).join(" & ");
  const time =
    cohort.session_start_time && cohort.session_end_time
      ? `${cohort.session_start_time}–${cohort.session_end_time}${cohort.timezone ? ` ${cohort.timezone}` : ""}`
      : "";
  const main = [days, time].filter(Boolean).join(", ");
  return [main, cohort.schedule_note].filter(Boolean).join(" · ");
};

/** Whole weeks between the two dates the admin entered. */
export const durationWeeks = (cohort) => {
  if (!cohort?.start_date || !cohort?.end_date) return null;
  const weeks = (new Date(cohort.end_date) - new Date(cohort.start_date)) / (7 * 86400000);
  return Math.max(1, Math.round(weeks));
};

/** One place decides which price is charged and whether a discount still runs. */
export const priceOf = (cohort) => {
  const currency = cohort?.currency || "INR";
  const listed = cohort?.price_actual ?? 0;
  const discounted = cohort?.price_discounted;
  const daysLeft = daysUntil(cohort?.discount_ends_on);
  const discountLive = Boolean(discounted) && (cohort?.discount_ends_on ? daysLeft >= 0 : true);

  return {
    currency,
    listed,
    payable: discountLive ? discounted : listed,
    discountLive,
    discount_ends_on: cohort?.discount_ends_on || null,
    discountDaysLeft: daysLeft,
  };
};

export const money = (amount, currency = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount || 0);
